import { useEffect, useState } from "react";
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, TextField,
  DialogActions, DialogContent, DialogTitle, Divider, LinearProgress, Stack, Typography,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import API, { getErrorMessage } from "../api/axios";

export default function AiCvMatchDialog({ candidate, jobs, onClose }) {
  const [job, setJob] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [configured, setConfigured] = useState(null);

  useEffect(() => {
    if (!candidate) {
      setConfigured(null);
      return undefined;
    }
    let active = true;
    setConfigured(null);
    API.get("/ai/status")
      .then(({ data }) => { if (active) setConfigured(Boolean(data.enabled)); })
      .catch(() => { if (active) setConfigured(false); });
    return () => { active = false; };
  }, [candidate]);

  useEffect(() => {
    const current = jobs.find((item) => item.jobCode === candidate?.jobCode);
    setJob(current || null);
  }, [candidate, jobs]);

  const runMatch = async () => {
    if (!candidate || !job) return;
    setBusy(true);
    setError("");
    setResult(null);
    try {
      const { data } = await API.post("/ai/match", { candidateId: candidate.id, jobCode: job.jobCode });
      setResult(data);
    } catch (err) {
      setError(getErrorMessage(err, "Could not compare this CV with the position."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={Boolean(candidate)} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1 }}>
        <AutoAwesomeIcon color="primary" fontSize="small" />
        <Box>
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>CV to position match</Typography>
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>{candidate ? `${candidate.firstName || ""} ${candidate.lastName || ""}`.trim() || `Candidate #${candidate.id}` : ""}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Autocomplete
            options={jobs}
            value={job}
            onChange={(_, value) => { setJob(value); setResult(null); }}
            getOptionLabel={(item) => `${item.jobCode || ""} · ${item.roleName || "Position"}${item.companyName ? ` · ${item.companyName}` : ""}`}
            isOptionEqualToValue={(a, b) => a.jobCode === b.jobCode}
            renderInput={(params) => <TextField {...params} label="Compare against position" size="small" />}
          />
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            The matcher compares CV evidence with the position requirements. Use the score as a review aid, not as an automatic hiring decision.
          </Typography>
          {configured === null && <Alert severity="info">Checking AI availability…</Alert>}
          {configured === false && <Alert severity="info">CV matching is not active yet. Ask your CRM administrator to configure the AI provider on the backend.</Alert>}
          {busy && <Box><LinearProgress /><Typography sx={{ mt: 1, fontSize: 12, color: "text.secondary" }}>Reading CV and comparing requirements…</Typography></Box>}
          {error && <Alert severity="error">{error}</Alert>}
          {result && (
            <Stack spacing={1.5}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <Typography sx={{ fontWeight: 800, fontSize: 30, color: "primary.main" }}>{result.score}%</Typography>
                <Box sx={{ flex: 1 }}><LinearProgress variant="determinate" value={result.score} sx={{ height: 8, borderRadius: 8 }} /><Typography sx={{ fontSize: 11, color: "text.secondary", mt: 0.5 }}>Match estimate</Typography></Box>
              </Box>
              <Typography sx={{ fontSize: 13, lineHeight: 1.6 }}>{result.summary}</Typography>
              <Divider />
              <SkillGroup title="Matched skills" items={result.matchedSkills} color="success" />
              <SkillGroup title="Gaps to review" items={result.gaps} color="warning" />
              <SkillGroup title="CV evidence" items={result.evidence} color="default" />
              <Alert severity="info" icon={false}><Typography sx={{ fontWeight: 700, fontSize: 12, mb: 0.35 }}>Suggested next step</Typography><Typography sx={{ fontSize: 13 }}>{result.recommendation}</Typography></Alert>
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 2, py: 1.5 }}>
        <Button onClick={onClose} disabled={busy}>Close</Button>
        <Button variant="contained" startIcon={busy ? <CircularProgress size={15} color="inherit" /> : <AutoAwesomeIcon />} onClick={runMatch} disabled={!job || busy || configured !== true}>
          {result ? "Run again" : "Match CV"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SkillGroup({ title, items = [], color }) {
  if (!items.length) return null;
  return <Box><Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.7 }}>{title}</Typography><Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">{items.map((item, i) => <Chip key={`${item}-${i}`} label={item} size="small" color={color === "default" ? "default" : color} variant={color === "default" ? "outlined" : "filled"} sx={{ maxWidth: "100%" }} />)}</Stack></Box>;
}
